<template>
  <div class="max-w-full mx-auto px-6 py-6 dark:bg-gray-600 dark:border-gray-600">
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
        Job <span class="font-mono">#{{ $route.params.id }}</span>
      </h2>
      <button @click="$router.back()"
        class="rounded-md bg-gray-200 dark:bg-gray-500 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-400">&larr;
        Back</button>
    </div>

    <div v-if="loading"></div>
    <div v-else-if="!job" class="py-10 text-center text-sm text-red-500">Job not found.</div>

    <div v-else class="space-y-6">
      <section class="rounded-md border border-gray-200 dark:border-gray-500 bg-white dark:bg-gray-700 p-4">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Field label="Date" :value="formatDate(job.jdate)" />
          <Field label="Company" :value="job.company_name" />
          <Field label="Tech" :value="job.tech_name" />
          <Field label="Hours" :value="formatHours(job.totalTime)" />
          <Field label="Billed" :value="job.billed" />
          <Field label="Travel Time" :value="formatHours(job.travelTime)" />
          <Field label="Miles" :value="job.miles" />
          <Field label="Start" :value="formatDate(job.startTime)" />
        </div>
      </section>

      <TextBlock label="Description" :value="job.description" />
      <TextBlock label="Details" :value="job.details" />

      <section v-if="job.parent_ticket"
        class="rounded-md border border-gray-200 dark:border-gray-500 bg-white dark:bg-gray-700 p-4">
        <h3 class="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Ticket</h3>
        <div class="flex gap-3 items-center mb-2">
          <RouterLink :to="{ path: `/ticket/${job.parent_ticket.id}` }"
            class="text-blue-600 dark:text-blue-300 font-mono text-sm hover:underline">#{{ job.parent_ticket.id }}
          </RouterLink>
        </div>
        <div class="text-sm text-gray-700 dark:text-gray-100">
          <div><span class="font-medium">Company:</span> {{ job.parent_ticket.company_name || '—' }}</div>
          <div><span class="font-medium">Submitted:</span> {{ formatDate(job.parent_ticket.submitted) }}</div>
          <div><span class="font-medium">Status:</span> {{ parentStatusLabel }}</div>
          <div class="mt-1" v-html="renderMarkdown(job.parent_ticket.description, { compact: true, maxLines: 10 })"></div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { renderMarkdown } from '@/utils/markdown';
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import axios from 'axios';
import Field from './_Field.vue';
import TextBlock from './_TextBlock.vue';

const route = useRoute();
const job = ref(null);
const loading = ref(true);
const { proxy } = getCurrentInstance();
const updateLoading = (n) => proxy?.$updateLoading?.(n);

const parentStatusLabel = computed(() => {
  const c = job.value?.parent_ticket?.completed;
  if (c === undefined) return '—';
  return (!c || String(c).startsWith('0000')) ? 'Open' : 'Closed';
});

async function load(id) {
  loading.value = true;
  updateLoading(1);
  try {
    const { data } = await axios.get(`reporting/jobs/${id}`);
    job.value = data;
  } catch (e) { job.value = null; }
  finally { loading.value = false; updateLoading(-1); }
}

function formatDate(d) {
  if (!d || String(d).startsWith('0000')) return '—';
  return String(d).substring(0, 19);
}
function formatHours(h) {
  if (h === null || h === undefined) return '—';
  return Number(h).toFixed(2);
}
watch(() => route.params.id, (id) => id && load(id));
onMounted(() => load(route.params.id));
</script>
